import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Button } from "@/components/design/Button";
import { Section } from "@/components/design/Section";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
  createLabSession,
  deleteLabSession,
  fetchLabSessions,
  TobisNotConfiguredError,
  type TobisLabSessionSummary,
} from "@/lib/tobis";
import { logApplicationFailure } from "@/lib/user-error";
import { formatDateTime } from "@/lib/format-date";
import { TobisPageShell } from "./TobisPageShell";

// Labor-Landing: die vorgelagerte Seite vor dem eigentlichen Editor, im
// Zuschnitt von MATCH (MatchPage.tsx) - "deine bisherigen Untersuchungen" +
// eine Karte je Labor-Sitzung (V0034 im BFF), "Neue Untersuchung" legt eine
// leere Sitzung an und navigiert direkt in TobisLabSessionPage.
//
// Anders als bei Match kein Draft-Modal: das Labor hat kein Pflichtfeld
// vorab (kein matchMode, keine Zugriffsliste) - der Pitch selbst wird erst
// in Schritt 1 des Editors erfasst.

export function TobisLabPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<TobisLabSessionSummary[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "notConfigured" | "error">("loading");
  const [creating, setCreating] = useState(false);
  // ADR-0011: eine Untersuchung ist ein Aggregat (Pitch + Kriterien + der
  // ganze bisherige Editor-Stand), kein First-Click-Delete - ConfirmDialog
  // ist hier der kanonische Fall, nicht das Inline-Zweischritt-Pill.
  const [pendingDelete, setPendingDelete] = useState<TobisLabSessionSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchLabSessions()
      .then((s) => {
        if (cancelled) return;
        setSessions(s);
        setPhase("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof TobisNotConfiguredError) {
          setPhase("notConfigured");
          return;
        }
        logApplicationFailure("tobis.lab.landing");
        setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    try {
      const session = await createLabSession();
      navigate(`/tobis/lab/${session.id}`);
    } catch {
      logApplicationFailure("tobis.lab.landing.create");
      setCreating(false);
    }
  }

  async function handleDelete(session: TobisLabSessionSummary) {
    if (deleting) return;
    setDeleting(true);
    try {
      const ok = await deleteLabSession(session.id);
      if (ok) setSessions((prev) => prev.filter((s) => s.id !== session.id));
    } catch {
      logApplicationFailure("tobis.lab.landing.delete");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  const hint =
    phase === "notConfigured"
      ? intl.formatMessage({
          id: "tobis.notConfigured",
          defaultMessage:
            "No database configured. Set TOBIS_DATABASE_URL in the BFF environment and run the migration.",
        })
      : phase === "error"
        ? intl.formatMessage({ id: "tobis.loadError", defaultMessage: "The overview could not be loaded." })
        : undefined;

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={intl.formatMessage({ id: "tobis.lab.heading", defaultMessage: "Lab" })}
      intro={intl.formatMessage({
        id: "tobis.lab.landing.intro",
        defaultMessage:
          "Every comparison-film investigation you've started. Pick one up where you left off, or start a new one.",
      })}
    >
      {hint ? (
        <Section title={intl.formatMessage({ id: "tobis.lab.heading", defaultMessage: "Lab" })}>
          <p className="text-sm text-ink/55">{hint}</p>
        </Section>
      ) : (
        <Section
          title={intl.formatMessage({
            id: "tobis.lab.landing.yourSessions",
            defaultMessage: "Your investigations",
          })}
          right={
            <Button variant="primary" size="compact" onClick={() => void handleCreate()} disabled={creating}>
              {creating
                ? intl.formatMessage({ id: "tobis.lab.landing.creating", defaultMessage: "Creating…" })
                : intl.formatMessage({ id: "tobis.lab.landing.newSession", defaultMessage: "New investigation" })}
            </Button>
          }
          empty={phase === "ready" && sessions.length === 0}
          emptyLabel={intl.formatMessage({
            id: "tobis.lab.landing.empty",
            defaultMessage: "No investigations yet",
          })}
          emptyHint={intl.formatMessage({
            id: "tobis.lab.landing.emptyHint",
            defaultMessage: "Start one with the button above.",
          })}
        >
          {phase === "loading" && (
            <p role="status" className="text-sm text-ink/55">
              {intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}
            </p>
          )}
          <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
            {sessions.map((s) => {
              const label =
                s.name ||
                s.pitchTitle ||
                intl.formatMessage({ id: "tobis.lab.untitledPitch", defaultMessage: "untitled" });
              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/tobis/lab/${s.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") navigate(`/tobis/lab/${s.id}`);
                  }}
                  className="relative box bg-surface p-4 text-left transition hover:ring-accent/40"
                >
                  <div className="pr-6 text-sm font-medium">{label}</div>
                  {s.pitchShort && (
                    <div className="mt-1 line-clamp-2 text-[12px] leading-snug text-ink/70">{s.pitchShort}</div>
                  )}
                  <div className="mt-4 text-[10px] text-ink/45">
                    {intl.formatMessage(
                      { id: "tobis.lab.landing.updatedAt", defaultMessage: "Updated {when}" },
                      { when: formatDateTime(s.updatedAt, intl.locale) },
                    )}
                  </div>
                  {/* stopPropagation: die Karte selbst navigiert bei Klick, der
                      Delete-Knopf darf das nicht zusaetzlich ausloesen. */}
                  <div className="absolute right-2 top-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(s)}
                      title={intl.formatMessage({ id: "tobis.lab.landing.delete", defaultMessage: "Delete" })}
                      aria-label={intl.formatMessage(
                        { id: "tobis.lab.landing.deleteAria", defaultMessage: "Delete {label}" },
                        { label },
                      )}
                      className="rounded-full p-1 text-ink/40 transition hover:bg-red-600/10 hover:text-red-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={intl.formatMessage(
          { id: "tobis.lab.landing.confirmDeleteTitle", defaultMessage: "Delete “{label}”?" },
          {
            label:
              pendingDelete?.name ||
              pendingDelete?.pitchTitle ||
              intl.formatMessage({ id: "tobis.lab.untitledPitch", defaultMessage: "untitled" }),
          },
        )}
        message={intl.formatMessage({
          id: "tobis.lab.landing.confirmDeleteMessage",
          defaultMessage: "The investigation and its criteria are removed for good.",
        })}
        confirmLabel={intl.formatMessage({ id: "tobis.lab.landing.delete", defaultMessage: "Delete" })}
        onConfirm={() => {
          if (pendingDelete) void handleDelete(pendingDelete);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </TobisPageShell>
  );
}
